<script>
import { formatConversationDate } from '@modules/aawz-intelligence-v2/utils/formatConversationDate'
import { writeConversationMovePayload } from '@modules/aawz-intelligence-v2/utils/conversationMoveDrag'
import { PERSONAS } from '@modules/aawz-intelligence-v2/constants/Personas'
import { getHub } from '@modules/aawz-intelligence-v2/constants/Hubs'
import {
  hubDaConversa,
  MAPA_TRANSICAO_LEGADO_HUB,
} from '@modules/aawz-intelligence-v2/utils/agrupar-conversas'

// Delay antes de fechar o flyout ao sair do trigger — cobre o "corredor" entre
// o item e o painel (mesma tecnica de navegacao-v2/nav-flyout.vue).
const FLYOUT_CLOSE_DELAY_MS = 150
// Espaco minimo assumido pro flyout (casa com min-width do CSS) — usado pra
// decidir se abre a direita ou inverte pra esquerda quando nao couber.
const FLYOUT_MIN_WIDTH = 200
// Separa a data do rotulo secundario na linha auxiliar ("ontem · Pastas").
const SEPARADOR_META = ' · '

// Linha de conversa (solta ou dentro de pasta). Fonte de drag, rename inline,
// e menu de ações com "Mover para pasta" (flyout, estilo navegação 2026).
export default {
  name: 'IntelligenceV2ConversationRow',
  props: {
    // O chip do agente só faz sentido onde a lista MISTURA agentes (popup e
    // painel do Hub). Na sidebar da página cheia o agente já está selecionado
    // ao lado e o `metaLabel` termina com o nome dele — o chip duplicava a
    // informação e comia a largura do título.
    mostrarAgente: {
      type: Boolean,
      default: false,
    },
    conversation: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    folders: {
      type: Array,
      default: () => [],
    },
    /**
     * Esconde o balão da linha. Existe para a lateral do guia do Fee Fixo, que
     * segue o v0: lá o glifo fino vive no CABEÇALHO da seção e os itens são só
     * texto (G-D6). Default `false` — a coluna do IA Operacional não muda.
     */
    semIcone: {
      type: Boolean,
      default: false,
    },
    // Contexto do agrupamento (nome da pasta, do agente) mostrado ao lado da
    // data quando a linha aparece na sidebar agrupada. Default vazio: as
    // superfícies que já usavam a linha (popup, conversations.vue) não mudam.
    rotuloSecundario: {
      type: String,
      default: '',
    },
    // Linha do ChatGPT, usada só na lateral da `/ia`: sem ícone à esquerda, o
    // agente só no tooltip de um ícone à direita, e o metadado cedendo lugar ao
    // menu de ações no hover. Opt-in porque a MESMA linha roda no popup e na
    // rota cheia, que não mudam.
    estiloLimpo: {
      type: Boolean,
      default: false,
    },
    // Esconde "Mover para pasta" e "Remover da pasta". Opt-in para a seção
    // "Recentes", que é uma lista PLANA por data — falar de pasta ali oferece
    // uma operação sobre um agrupamento que aquela superfície não mostra.
    // Renomear e Remover ficam: são ações da conversa, não da pasta.
    semAcoesDePasta: {
      type: Boolean,
      default: false,
    },
    // Liga o Ctrl/Cmd+click como seleção em lote (ver `handleSelect`) em vez
    // de abrir a conversa. Opt-in porque esta linha é COMPARTILHADA com o
    // popup e a rota cheia — sem a flag, ctrl+click nessas superfícies
    // continua idêntico a um clique normal.
    permitirSelecaoLote: {
      type: Boolean,
      default: false,
    },
    // Selecionada para a ação em lote — visual DIFERENTE de `selected`
    // (que significa "é a conversa aberta agora"): as duas podem ser
    // verdadeiras ao mesmo tempo sem se confundir.
    selectedForBatch: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editing: false,
      editingTopic: '',
      moveFlyoutOpen: false,
      moveFlyoutPosition: { top: 0, left: 0 },
      moveFlyoutCloseTimer: null,
    }
  },
  computed: {
    topicLabel() {
      return (this.conversation && this.conversation.topic) || 'Nova conversa'
    },
    dateLabel() {
      return formatConversationDate(
        this.conversation && this.conversation.updated_at
      )
    },
    // A data continua saindo de `formatConversationDate` (AC-5.6); o rotulo
    // secundario so se junta a ela. Composto aqui e nao no template para o
    // separador nao virar literal solto no markup.
    //
    // ⚠️ O modo limpo já removeu este sufixo uma vez (27/08), quando
    // "Recentes" ainda filtrava por agente ativo — ali o nome seria CONSTANTE
    // em toda linha (lista de um agente só) e só enchia a direita à toa. Com a
    // lista virando multi-agente, cada linha pode ser de um agente diferente
    // e o texto volta a informar algo — por isso o modo limpo passou a seguir
    // a mesma regra do modo padrão, em vez de um ramo próprio.
    metaLabel() {
      if (!this.rotuloSecundario) return this.dateLabel
      if (!this.dateLabel) return this.rotuloSecundario
      return this.dateLabel + SEPARADOR_META + this.rotuloSecundario
    },
    currentFolderId() {
      return (this.conversation && this.conversation.id_folder) || null
    },
    // Só "Renomear" fica no BaseActionsMenu.actions — as demais linhas
    // (Mover pra pasta / Remover da pasta / Remover) são renderizadas no slot
    // pra ficarem logo abaixo de Renomear (BaseActionsMenu é flat: actions
    // sempre renderiza antes do slot, sem interleaving — nao pode ser
    // modificado).
    rowActions() {
      return [
        { label: 'Renomear', icon: 'edit-pencil', onClick: this.startEdit },
      ]
    },
    // Opções do flyout "Mover para pasta" — só as OUTRAS pastas (a atual já
    // tem "Remover da pasta" como linha própria). Some quando não há nenhuma
    // outra pasta pra mover.
    folderPickerItems() {
      return this.folders
        .filter((f) => f && f.id !== this.currentFolderId)
        .map((f) => ({
          key: f.id,
          label: f.name,
          color: f.color || null,
          folderId: f.id,
        }))
    },
    // As duas linhas de pasta do menu, cada uma com a sua própria condição de
    // existência (ter outra pasta / estar em uma), somadas ao opt-in de quem
    // não quer nenhuma delas.
    mostrarMoverParaPasta() {
      if (this.semAcoesDePasta) return false
      return this.folderPickerItems.length > 0
    },
    mostrarRemoverDaPasta() {
      if (this.semAcoesDePasta) return false
      return Boolean(this.currentFolderId)
    },
    // left OU right (nunca os dois) — moveFlyoutPosition so preenche um lado
    // por vez, dependendo se coube a direita do trigger (ver openMoveFlyout).
    moveFlyoutStyle() {
      return {
        top: this.moveFlyoutPosition.top + 'px',
        left: this.moveFlyoutPosition.left + 'px',
      }
    },
    // Rótulo do HUB (D6) da conversa — espelho FRONT de `hub_da_conversa`
    // (spec-010 [T029], US-7: "chip da conversa passa a exibir o hub").
    // `hubDaConversa` aceita tanto um `context` já-hub quanto os 3 valores de
    // PERSONA legados, e nunca lança — vazio só quando não há conversa.
    hubLabel() {
      if (!(this.conversation && this.conversation.context)) return ''
      return getHub(hubDaConversa(this.conversation.context)).label
    },
    // Nome curto do ÚLTIMO agente, só quando `context` é um dos 3 valores de
    // PERSONA que `MAPA_TRANSICAO_LEGADO_HUB` migra (T029) — id que NÃO está
    // nesse mapa não tem agente conhecido neste nível (a conversa pode ter
    // tido vários agentes por turno; ver `agent-badge` para o agente POR
    // MENSAGEM). Guarda por `Object.hasOwn` no MAPA DE TRANSIÇÃO, e não em
    // `PERSONAS` inteiro: `PERSONAS` também tem a entrada `AI_FRAMEWORK`, que
    // é OUTRO eixo (`origem_modulo`+`id_instance`, ADR-34.10) — mostrá-la
    // aqui diria que aquela conversa passou pelo roteamento de hub, o que
    // nunca aconteceu.
    agenteLegadoLabel() {
      const contexto = this.conversation && this.conversation.context
      if (!contexto || !Object.hasOwn(MAPA_TRANSICAO_LEGADO_HUB, contexto)) {
        return ''
      }
      return (PERSONAS[contexto] && PERSONAS[contexto].shortName) || ''
    },
    // Chip da conversa: hub sempre; e, quando houver (context ainda legado),
    // o último agente ao lado — ui-components.md §3 (`conversation-row.vue`
    // "chip da conversa passa a exibir o hub e, quando houver, o último
    // agente"). Nome mantido (`agenteLabel`) para o contrato do template e de
    // `conversations.vue` (popup, D-8) não mudar.
    agenteLabel() {
      if (!(this.conversation && this.conversation.context)) return ''
      if (this.agenteLegadoLabel) {
        return this.hubLabel + ' · ' + this.agenteLegadoLabel
      }
      return this.hubLabel
    },
  },
  beforeDestroy() {
    this.clearMoveFlyoutCloseTimer()
    document.removeEventListener('mousedown', this.onDocumentMouseDown, true)
  },
  methods: {
    handleSelect(event) {
      if (this.editing) return
      // Ctrl/Cmd+click vira toggle de seleção em lote em vez de abrir a
      // conversa — mesmo check que `aawz-drive-selection-area.vue` usa
      // (único precedente de ctrl-click no repo), sem a máquina de
      // rubber-band/shift-range de lá, que não foi pedida aqui.
      if (
        this.permitirSelecaoLote &&
        event &&
        (event.ctrlKey || event.metaKey)
      ) {
        this.$emit('toggle-select', this.conversation.id)
        return
      }
      this.$emit('select', this.conversation)
    },
    onDragStart(event) {
      if (this.editing) return
      const dt = event ? event.dataTransfer : null
      writeConversationMovePayload(dt, {
        conversationId: this.conversation.id,
        currentFolderId: this.currentFolderId,
      })
      if (dt) dt.effectAllowed = 'move'
    },
    startEdit() {
      this.editing = true
      this.editingTopic = (this.conversation && this.conversation.topic) || ''
      this.$nextTick(() => {
        const el = this.$refs.renameInput
        if (el) {
          el.focus()
          el.select()
        }
      })
    },
    saveEdit() {
      if (!this.editing) return
      const newTopic = (this.editingTopic || '').trim()
      if (newTopic && newTopic !== (this.conversation.topic || '')) {
        this.$emit('rename', { id: this.conversation.id, topic: newTopic })
      }
      this.editing = false
      this.editingTopic = ''
    },
    cancelEdit() {
      this.editing = false
      this.editingTopic = ''
    },
    requestDelete() {
      this.$emit('request-delete', this.conversation)
    },
    // Remove da pasta atual = mover pra "sem pasta" (mesmo contrato do drag
    // pra fora de uma pasta, ver conversations.vue.onLooseDrop).
    removeFromFolder() {
      this.$emit('move', {
        conversationId: this.conversation.id,
        folderId: null,
      })
    },
    pickFolder(option) {
      this.closeMoveFlyout()
      this.$emit('move', {
        conversationId: this.conversation.id,
        folderId: option.folderId,
      })
    },
    clearMoveFlyoutCloseTimer() {
      if (this.moveFlyoutCloseTimer) {
        clearTimeout(this.moveFlyoutCloseTimer)
        this.moveFlyoutCloseTimer = null
      }
    },
    openMoveFlyout() {
      if (!this.mostrarMoverParaPasta) return
      this.clearMoveFlyoutCloseTimer()
      const el = this.$refs.moveTrigger
      if (el) {
        const rect = el.getBoundingClientRect()
        // Prefere abrir pra direita (sidebar de conversas eh estreita, sem
        // espaco a esquerda) — mas o mesmo componente tambem roda dentro do
        // popup flutuante (chat-ia-pop-up.vue), ancorado no canto direito da
        // tela, onde "direita" pode nao caber. Inverte quando faltar espaco.
        const spaceRight = window.innerWidth - rect.right
        const opensLeft = spaceRight < FLYOUT_MIN_WIDTH
        this.moveFlyoutPosition = {
          top: rect.top,
          left: opensLeft
            ? Math.max(8, rect.left - FLYOUT_MIN_WIDTH)
            : rect.right,
        }
      }
      if (!this.moveFlyoutOpen) {
        document.addEventListener('mousedown', this.onDocumentMouseDown, true)
      }
      this.moveFlyoutOpen = true
    },
    toggleMoveFlyout() {
      if (this.moveFlyoutOpen) {
        this.closeMoveFlyout()
      } else {
        this.openMoveFlyout()
      }
    },
    scheduleCloseMoveFlyout() {
      this.clearMoveFlyoutCloseTimer()
      this.moveFlyoutCloseTimer = setTimeout(() => {
        this.closeMoveFlyout()
      }, FLYOUT_CLOSE_DELAY_MS)
    },
    closeMoveFlyout() {
      this.clearMoveFlyoutCloseTimer()
      this.moveFlyoutOpen = false
      document.removeEventListener('mousedown', this.onDocumentMouseDown, true)
    },
    // O flyout renderiza como position:fixed fora do overflow:hidden do
    // BaseActionsMenu — precisa do proprio listener de clique-fora (o
    // outside-click do v-menu so enxerga o conteudo dele mesmo).
    onDocumentMouseDown(event) {
      const trigger = this.$refs.moveTrigger
      const flyout = this.$refs.moveFlyout
      const target = event.target
      if (trigger && trigger.contains(target)) return
      if (flyout && flyout.contains(target)) return
      this.closeMoveFlyout()
    },
  },
}
</script>

<template>
  <div
    class="conversation-row d-flex align-center py-1 pr-2"
    :class="{
      'conversation-row--active': selected,
      'conversation-row--limpo': estiloLimpo,
      'conversation-row--batch-selected': selectedForBatch,
    }"
    :draggable="!editing"
    @click="handleSelect($event)"
    @dragstart="onDragStart"
  >
    <!-- O balão sai no modo limpo: numa coluna em que TODA linha é uma
         conversa, ele não distingue nada e só recua o título.
         As DUAS props escondem, por motivos independentes e em superfícies
         diferentes: `semIcone` é a lateral do guia do Fee Fixo, onde o glifo
         fino vive no CABEÇALHO da seção (G-D6, `fee-fixo-guia-conversas.vue`
         :199 e :284); `estiloLimpo` é a linha do ChatGPT na lateral da `/ia`
         (`chat-sidebar.vue:673`, `chat-sidebar-recents.vue:117`). Guardar só
         uma delas devolveria o balão à outra superfície. -->
    <icon-svg
      v-if="!semIcone && !estiloLimpo"
      name="message"
      :size="14"
      color="#8E8E8E"
      class="conversation-row__icon mr-2 flex-shrink-0"
    />

    <input
      v-if="editing"
      ref="renameInput"
      v-model="editingTopic"
      class="conversation-row__rename az-ds-body flex-grow-1 py-0 px-1"
      maxlength="100"
      aria-label="Renomear conversa"
      @keydown.enter.prevent="saveEdit"
      @keydown.esc.prevent="cancelEdit"
      @blur="saveEdit"
      @click.stop
    />

    <!-- Título e horário na MESMA linha: empilhados, cada conversa ocupava 47px
         e meia dúzia delas já enchia o painel. Lado a lado a lista cabe no
         dobro do conteúdo, e o horário vira metadado à direita, que é onde o
         olho não tropeça nele ao varrer os títulos. -->
    <div v-else class="conversation-row__info d-flex align-center flex-grow-1">
      <span
        class="conversation-row__label ia-texto-auxiliar az-ds-body text-truncate"
        :title="topicLabel"
        @dblclick.stop="startEdit"
        >{{ topicLabel }}</span
      >
      <!-- De qual agente é a conversa. Discreto de propósito: é metadado para
           quando o gestor não lembra de quem era, não uma etiqueta para ler em
           toda linha. Sem `agenteLabel` (conversa antiga sem `context`) o chip
           simplesmente não existe. -->
      <span
        v-if="mostrarAgente && agenteLabel"
        class="conversation-row__agente ia-texto-micro"
      >{{
        agenteLabel
      }}</span>
      <span class="conversation-row__date az-ds-aux text-truncate">{{
        metaLabel
      }}</span>
    </div>

    <template v-if="!editing">
      <div class="conversation-row__menu flex-shrink-0" @click.stop>
        <BaseActionsMenu
          icon-only
          label="Ações da conversa"
          :actions="rowActions"
        >
          <!-- padding:4px 4px 4px (sem top) casa com o inset horizontal do
               .base-actions-menu__list nativo (padding:4px) — sem este wrapper,
               os itens do slot ficam colados nas bordas do panel enquanto
               "Renomear" (dentro da __list) fica recuado 4px, gerando
               inconsistencia visual no hover/highlight. -->
          <div class="conversation-row__menu-slot">
            <button
              v-if="mostrarMoverParaPasta"
              ref="moveTrigger"
              type="button"
              class="conversation-row__menu-item ia-texto-corpo"
              @click.stop="toggleMoveFlyout"
              @mouseenter="openMoveFlyout"
              @mouseleave="scheduleCloseMoveFlyout"
            >
              <icon-svg name="folder-outline" :size="15" color="#8E8E8E" />
              <span class="flex-grow-1">Mover para pasta</span>
              <icon-svg name="mdi-chevron-right" :size="16" color="#8E8E8E" />
            </button>

            <button
              v-if="mostrarRemoverDaPasta"
              type="button"
              class="conversation-row__menu-item ia-texto-corpo"
              @click="removeFromFolder"
            >
              <icon-svg name="close" :size="15" color="#8E8E8E" />
              <span>Remover da pasta</span>
            </button>

            <button
              type="button"
              class="conversation-row__menu-item ia-texto-corpo conversation-row__menu-item--danger"
              @click="requestDelete"
            >
              <icon-svg name="trash" :size="15" color="danger" />
              <span>Remover</span>
            </button>
          </div>
        </BaseActionsMenu>
      </div>

      <!-- Irmão de .conversation-row__menu (NAO filho) -- .conversation-row__menu
           tem opacity:0 fora do hover da linha, e opacity afeta toda a subarvore
           renderizada mesmo com position:fixed (ao contrario de overflow, que o
           fixed escapa). Filho, o flyout ficava com moveFlyoutOpen=true mas
           invisivel assim que o mouse saia da linha em direcao ao trigger
           teleportado (BaseActionsMenu manda o slot pro <body> via v-menu). -->
      <div
        v-if="moveFlyoutOpen"
        ref="moveFlyout"
        class="conversation-row__flyout"
        :style="moveFlyoutStyle"
        @click.stop
        @mouseenter="clearMoveFlyoutCloseTimer"
        @mouseleave="scheduleCloseMoveFlyout"
      >
        <span
          class="conversation-row__flyout-title az-ds-label d-block pt-1 pb-1 px-3"
        >
          Mover para pasta
        </span>
        <button
          v-for="opt in folderPickerItems"
          :key="opt.key"
          type="button"
          class="conversation-row__move-item az-ds-body d-flex align-center az-gap-small py-2 px-3"
          @click="pickFolder(opt)"
        >
          <span
            v-if="opt.color"
            class="conversation-row__swatch flex-shrink-0"
            :style="{ backgroundColor: opt.color }"
          />
          <icon-svg
            v-else
            name="close"
            :size="13"
            color="#8E8E8E"
            class="conversation-row__swatch-icon flex-shrink-0"
          />
          <span class="text-truncate">{{ opt.label }}</span>
        </button>
      </div>
    </template>
  </div>
</template>

<style lang="sass" scoped>

.conversation-row
  border-radius: var(--radius-sm)
  cursor: pointer
  transition: background 0.12s
  // Recuo de um nível: a conversa está DENTRO da seção (Recentes, uma pasta,
  // Sem pasta). Alinhada com o rótulo da seção, a lista lia como uma coluna
  // plana e a hierarquia sumia.
  padding-left: 20px

  &:hover
    background: var(--neutral-100)

  &--active
    background: var(--neutral-100)
    outline: 1px solid var(--border-hairline)
    outline-offset: -1px

  // Seleção em lote (ctrl+click) — FUNDO, não outline: com várias linhas
  // vizinhas selecionadas ao mesmo tempo, um outline por linha soma nas
  // bordas que se tocam e vira costura dupla entre elas. Fundo não tem esse
  // problema em nenhuma quantidade de linhas adjacentes.
  //
  // `color-mix`, não `var(--primary)` puro nem um rgba() literal:
  // `--primary` é por ESCRITÓRIO (white-label) — um rgba() com o valor
  // decomposto (ex: rgba(175, 56, 255, .1)) travaria na cor DESTE
  // escritório e ficaria errado em qualquer outro. `color-mix` dilui
  // dinamicamente o `--primary` de quem estiver logado. Mesmo precedente já
  // usado em `disclaimer-ia.vue:73`
  // (`color-mix(in srgb, var(--primary) 15%, transparent)`). Não
  // `--neutral-100` de `--active`, pra não se confundir com "conversa
  // aberta agora" — as duas classes podem coexistir na mesma linha ao
  // mesmo tempo.
  &--batch-selected
    background: color-mix(in srgb, var(--primary) 10%, transparent)

  &__info
    min-width: 0

  // Sem `max-width`: o cap de 140px vinha do popup e truncava o título 20-35px
  // antes do necessário no trilho da sidebar (medido nos 288px da configuração
  // de histórico: sobram ~175px em Recentes e ~159px em Pastas; na imersiva, de
  // 400px, sobra ainda mais). A elipse continua garantida pelo `text-truncate`
  // da classe + `min-width: 0` no `&__info`.
  &__label
    display: block
    flex: 1 1 auto
    min-width: 0
    line-height: 1.35
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

  // Chip do agente: peso e cor de metadado, nunca competindo com o título.
  // Sem fundo forte — um chip colorido por linha viraria a coisa mais visível
  // da lista, que é exatamente o que o título deveria ser.
  &__agente
    color: var(--neutral-600)
    flex-shrink: 0
    margin-left: 8px
    padding: 1px 6px
    border-radius: 999px
    background: var(--neutral-100)
    white-space: nowrap

  // `flex: 0 1` e não `flex-shrink: 0`: no trilho de 288px da sidebar da página
  // cheia (o mais apertado dos dois — a imersiva tem 400px) o meta carrega o
  // nome do agente ("há 1h · IA Operacional") e, sem poder encolher, engolia o
  // título até sobrar "O que…". O título é o conteúdo; o meta é que trunca.
  &__date
    flex: 0 1 auto
    min-width: 0
    max-width: 55%
    margin-left: 8px
    line-height: 1.2
    // Sobrescreve o #8E8E8E de `az-ds-aux`, que reprova AA como texto: 3.28
    // sobre o branco do popup e 2.71 sobre --sidebar-warm-bg, onde esta linha
    // passa a ser montada pela chat-sidebar. --neutral-700 da 7.73 e 6.98.
    // O tamanho/familia continuam vindo da classe do DS.
    color: var(--neutral-700)

  &__rename
    flex: 1
    min-width: 0
    border: 1px solid var(--primary)
    border-radius: var(--radius-sm)
    outline: none
    background: white
    line-height: 1.5

  &__menu
    opacity: 0
    transition: opacity 0.12s

  &:hover &__menu,
  &--active &__menu
    opacity: 1

  &__menu-slot
    padding: 0 4px 4px

  // Replica visual do .base-actions-menu__item (scoped ao componente base,
  // nao reutilizavel daqui) pra manter as linhas custom do slot identicas
  // as nativas (Renomear).
  &__menu-item
    color: var(--neutral-950)
    position: relative
    display: flex
    align-items: center
    gap: 8px
    width: 100%
    padding: 8px 10px
    text-align: left
    cursor: pointer
    background: none
    border: 0
    border-radius: var(--radius-sm)

    &:hover
      background: var(--neutral-100)

    &--danger
      margin-top: 5px
      color: var(--danger-text)

      &::before
        position: absolute
        top: -3px
        right: 0
        left: 0
        height: 1px
        content: ''
        background: var(--border-hairline)

  // Flyout: position:fixed, renderizado como irmao de .conversation-row__menu
  // (ver template) -- precisa ficar FORA dela porque __menu tem opacity:0 fora
  // do hover da linha, e opacity (diferente de overflow) afeta toda a
  // subarvore renderizada mesmo com fixed escapando do layout.
  &__flyout
    position: fixed
    z-index: 250
    min-width: 200px
    max-width: 260px
    padding: 4px
    background: #fff
    border: 1px solid var(--border-hairline)
    border-radius: var(--radius-md)
    box-shadow: 0 12px 32px rgba(20, 30, 50, 0.14)

  &__flyout-title
    color: var(--neutral-600)

  &__move-item
    width: 100%
    text-align: left
    background: none
    border: 0
    border-radius: var(--radius-sm)
    cursor: pointer

    &:hover
      background: var(--neutral-100)

  &__swatch
    width: 12px
    height: 12px
    border-radius: 50%

// ★ Modo limpo (lateral da `/ia`): em repouso a linha mostra a hora; no hover
// ela cede o lugar às ações, como no ChatGPT. Um passa por CIMA do outro — o
// menu sai do fluxo — para o título não mudar de largura no caminho.
//
// ⚠️ Bloco no FIM do arquivo de propósito. Em Sass INDENTADO, uma regra de
// nível zero no meio faz tudo o que vem depois virar filho dela: posto entre o
// `&__menu` e o `&__menu-slot`, o `&` passou a resolver para
// `.conversation-row--limpo` e o menu de ações perdeu o estilo inteiro — os
// itens do painel apareceram sem layout nenhum.
.conversation-row--limpo
  position: relative
  // ⚠️ O recuo horizontal NÃO mora aqui: ele é derivado da métrica do cabeçalho
  // da seção, que é de outro componente. Vive em `design/_ia-sidebar.sass`,
  // junto de `.ia-sidebar-row`.

  // ⚠️ Quem encolhe é o TÍTULO, nunca a hora. No modo padrão o `__date` é
  // `flex: 0 1 auto` com teto de 55%, então numa conversa de título longo ele
  // cedia espaço e truncava a si mesmo — a linha mostrava "há 28 …". A hora tem
  // largura própria e pequena; o título já tem `text-truncate` para ceder.
  .conversation-row__date
    flex: 0 0 auto
    max-width: none
    transition: opacity var(--motion-duration-fast) var(--motion-easing-standard)

  .conversation-row__menu
    position: absolute
    right: 8px

  // O `--active` entra junto porque nele o menu já fica aceso: os dois visíveis
  // ao mesmo tempo seria sobreposição, não troca.
  &:hover .conversation-row__date,
  &.conversation-row--active .conversation-row__date
    opacity: 0
</style>
